<template>
  <b-container>
    <b-row class="justify-center pt-2">
      <div class="col-12 align-items-center d-flex">
        <router-link :to="`/cart`" class="position-absolute align-middle">
          <h2 class="px-0 mb-0">
            <b-icon-arrow-left-circle
              class="back-arrow"
            />
          </h2>
        </router-link>

        <h1 class="text-center px- mt-1 mb-0 w-100">
          {{ order.code ? 'Success' : 'Checkout' }}
          <span class="mdi mdi-cart cart-icon" />
        </h1>
      </div>
      <div class="col-12 align-items-center">
        <template v-if="!loading && region && vendor && !order.code">
          <h4 class="mt-1 mb-2 text-center px-1">
            {{ vendor.name }}
          </h4>
        </template>
      </div>
    </b-row>
    <hr class="mt-2 mb-4">

    <b-row v-show="loading" key="loading" align-h="center">
      <Spinner :size="100" centered />
    </b-row>
    <div v-show="!loading">
      <b-row v-if="order && order.code !== null" key="success">
        <b-container class="checkout-container text-center">
          <template
            v-if="order.deliveryType === DELIVERY_TYPE_OPTIONS.scheduled"
          >
            <p>
              Order <b>{{ order.code }}</b> has been <b>Scheduled</b>.
            </p>
            <template v-if="order.type === ORDER_TYPES.delivery">
              <p>We'll remind you by email before it departs for:</p>
              <p>
                <u>{{ order.address }}</u>
              </p>
            </template>
            <template v-else-if="order.type === ORDER_TYPES.pickup">
              <p>
                We'll remind you by email
                {{ vendorRegion.prep_time_minutes }} minutes before it's ready
                for <b>Pickup</b> on
                {{ moment(order.scheduled).format('MMM D, h:mma') }} at:
              </p>
              <p>
                <u>{{ vendorRegion.address.autocomplete }}</u>
              </p>
            </template>
          </template>
          <template
            v-else-if="order.deliveryType === DELIVERY_TYPE_OPTIONS.subscribe"
          >
            <p>Thanks for Subscribing, {{ contact.name }}!</p>
            <template v-if="order.type === ORDER_TYPES.delivery">
              <p>
                We'll email you when your <b>Deliveries</b> are on the way to:
              </p>
              <p>
                <u>{{ order.address }}</u>
              </p>
            </template>
            <template v-else-if="order.type === ORDER_TYPES.pickup">
              <p>
                We'll email you {{ vendorRegion.prep_time_minutes }} minutes
                before your orders will be ready for <b>Pickup</b> at:
              </p>
              <p>
                <u>{{ vendorRegion.address.autocomplete }}</u>
              </p>
            </template>
          </template>
          <template v-else>
            <p>
              Order <b>{{ order.code }}</b> has been <b>Received</b>.
            </p>
            <template v-if="order.type === ORDER_TYPES.delivery">
              <p>We'll email you when it's on it's way to:</p>
              <p>
                <u>{{ order.address }}</u>
              </p>
            </template>
            <template v-else-if="order.type === ORDER_TYPES.pickup">
              <p>
                It will be ready for <b>Pickup</b> on
                {{ moment(order.scheduled).format('MMM D, h:mma') }} at:
              </p>
              <p>
                <u>{{ vendorRegion.address.autocomplete }}</u>
              </p>
            </template>
          </template>

          <p>
            Questions? Get support
            <a
              target="_blank"
              :href="`mailto:${vendorRegion.operator_email}`"
            >here</a>.
          </p>

          <b-row align-h="center" class="mt-5">
            <b-button class="btn-cta" to="/">
              Shop
            </b-button>
          </b-row>
        </b-container>
      </b-row>
      <b-row v-else>
        <b-container class="checkout-container">
          <h1 class="mb-3 text-center text-md-left">
            Date & Time
          </h1>
          <b-row
            v-if="vendorRegion.delivery_enabled || vendorRegion.enable_pickup"
            class="justify-space-around"
            :align-h="rotatingSubscription ? 'flex-start' : 'center'"
          >
            <b-col v-if="!rotatingSubscription" cols="12" lg="6">
              <b-form-group
                v-if="
                  cartProducts &&
                    cartProducts.length &&
                    !cartProducts[0].max_weekly_frequency &&
                    !cartProducts[0].subscription
                "
                label="Choose the Frequency"
              >
                <b-form-select
                  v-model="order.deliveryType"
                  class="d-inline"
                  prepend-icon="mdi-clock"
                  :options="options"
                />
              </b-form-group>
              <span
                v-if="order.deliveryType === DELIVERY_TYPE_OPTIONS.now"
                class="hint"
              >
                {{ vendor.name }} needs
                {{
                  moment
                    .duration(vendorRegion.prep_time_minutes, 'minutes')
                    .humanize(false, 'm')
                }}
                notice to prepare an order.
              </span>
              <p
                v-if="order.deliveryType === DELIVERY_TYPE_OPTIONS.scheduled"
                class="mt-3"
              >
                🕘 We'll see you later!
              </p>
            </b-col>

            <b-col
              v-if="order.deliveryType === DELIVERY_TYPE_OPTIONS.scheduled"
              cols="12"
              lg="6"
            >
              <b-row>
                <b-col md="12">
                  <b-form-group
                    :label="
                      orderType === ORDER_TYPES.delivery
                        ? 'Delivery Date'
                        : 'Pickup Date'
                    "
                  >
                    <b-input-group>
                      <b-form-datepicker
                        v-model="order.date"
                        class="date-picker"
                        :date-disabled-fn="dateDisabled"
                        :max="maxDate"
                        :min="minDate"
                        locale="en"
                        :placeholder="`Choose a ${
                          orderType === ORDER_TYPES.delivery
                            ? 'delivery date'
                            : 'pickup date'
                        }`"
                      />
                      <b-input-group-prepend>
                        <b-button
                          class="rounded-end"
                          variant="info"
                          @click="order.date = order.time = null"
                        >
                          <b-icon-x />
                        </b-button>
                      </b-input-group-prepend>
                    </b-input-group>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row v-if="orderType !== ORDER_TYPES.shipping">
                <b-col md="12">
                  <b-form-group
                    :label="
                      noSlotsOnDay
                        ? `${vendor.name} isn't open that day!`
                        : orderType === ORDER_TYPES.delivery
                          ? 'Delivery Window'
                          : 'Pickup Time'
                    "
                  >
                    <b-input-group>
                      <b-form-select
                        v-model="order.time"
                        :disabled="!order.date || noSlotsOnDay"
                        :options="
                          order.date
                            ? intervalOptions[
                              moment(order.date).format('dddd').toLowerCase()
                            ]
                            : []
                        "
                      />
                    </b-input-group>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-col>

            <template
              v-if="order.deliveryType === DELIVERY_TYPE_OPTIONS.subscribe"
            >
              <b-col v-if="cartProducts[0].subscription" cols="12">
                <p class="mt-3">
                  This is a subscription for:
                </p>
                <div v-if="rotatingSubscription" class="fixed-fulfillment-date">
                  <p class="fixed-fulfillment-text">
                    Your order will be ready on
                    {{
                      rotatingSubscription.fulfillment_day_time_string ||
                        getFormattedFulfillmentDate()
                    }}
                  </p>
                </div>
                <div v-else>
                  <p v-for="item in productSubscription" :key="item.day">
                    <span class="time">{{ item.day }}: </span>
                    <span>{{
                      moment(item.time, 'hh:mm:ss').format('h:mm A')
                    }}</span>
                  </p>
                </div>
              </b-col>

              <b-col
                v-else-if="!loading && rotatingSubscription"
                cols="12"
                lg="6"
              >
                <p class="mt-3">
                  🕘 Set it and forget it. We've got you.
                </p>
                <div class="fixed-fulfillment-date">
                  <p class="fixed-fulfillment-text">
                    Your order will be ready on
                    {{
                      rotatingSubscription.fulfillment_day_time_string ||
                        getFormattedFulfillmentDate()
                    }}
                  </p>
                </div>
              </b-col>

              <b-col v-else-if="!loading" cols="12" lg="6">
                <p class="mt-3">
                  🕘 Set it and forget it. We've got you.
                </p>
                <b-row>
                  <b-col cols="12">
                    <SubscriptionEditor
                      v-model="order.subscription"
                      :subscription="subscribedDays"
                      :vendor="vendor"
                      :order="order"
                      :delivery-schedule-intervals="deliveryScheduleIntervals"
                      :pickup-schedule-intervals="pickupScheduleIntervals"
                      :weekly-frequency="weeklyFrequency"
                      @updateSelectedDays="updateSelectedDays"
                    />
                  </b-col>
                </b-row>
              </b-col>
            </template>
          </b-row>

          <h1 class="mt-4 mb-2 text-center text-md-left">
            Your Details
          </h1>
          <b-form
            ref="form"
            class="contact-form"
            novalidate
            @submit.prevent="checkout"
          >
            <b-form-row align-h="center">
              <b-col md="4">
                <b-form-group label="Name">
                  <b-form-input
                    v-model.trim="contact.name"
                    :state="contact.name && contact.name.length > 1"
                    placeholder=""
                    required
                    type="text"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Email">
                  <b-form-input
                    v-model.trim="contact.email"
                    disabled
                    placeholder=""
                    required
                    type="email"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Phone">
                  <b-form-input
                    v-model.trim="contact.phone"
                    :state="contact.phone && contact.phone.length >= 10"
                    required
                    type="text"
                    placeholder="e.g., +1 555-123-4567 or +44 20 1234 5678"
                  />
                  <div id="credit-card-anchor" />
                </b-form-group>
              </b-col>
            </b-form-row>
          </b-form>

          <div>
            <h1
              id="credit-card-title"
              class="mt-4 mb-3 text-center text-md-left"
            >
              Billing
            </h1>
            <payment-methods v-model="order.payment_method_uuid" />
          </div>

          <h1 class="mt-5 mb-3 text-center text-md-left">
            Order Note
          </h1>
          <b-form-textarea
            id="textarea"
            v-model="order.note"
            style="border-radius: 5px !important"
            max-rows="6"
            maxlength="500"
            rows="3"
          />

          <template v-if="order.order_type === ORDER_TYPES.delivery">
            <h1 class="mt-5 mb-3 text-center text-md-left">
              Delivery Note
            </h1>
            <b-form-textarea
              id="textarea"
              v-model="order.delivery_note"
              style="border-radius: 5px !important"
              max-rows="6"
              maxlength="500"
              rows="3"
            />
          </template>

          <p
            v-if="order.deliveryType === DELIVERY_TYPE_OPTIONS.now"
            class="my-5 text-center"
          >
            {{ vendor.name }} needs
            {{
              moment
                .duration(vendorRegion.prep_time_minutes, 'minutes')
                .humanize(false, 'm')
            }}
            notice to prepare an order.
          </p>

          <b-row align-h="center" class="my-5">
            <b-button :disabled="false" class="btn-cta" @click="checkout">
              <template
                v-if="order.deliveryType === DELIVERY_TYPE_OPTIONS.scheduled"
              >
                Schedule
              </template>
              <template
                v-else-if="
                  order.deliveryType === DELIVERY_TYPE_OPTIONS.subscribe
                "
              >
                Subscribe
              </template>
              <template v-else>
                Continue
              </template>
            </b-button>
          </b-row>
        </b-container>
      </b-row>
    </div>
  </b-container>
</template>

<script>
import Spinner from '@/common/Spinner.vue';
import Address from '@/auth/Address.vue';
import PaymentMethods from '@/cart/PaymentMethods.vue';
import SubscriptionEditor from '@/subscriptions/SubscriptionEditor.vue';
import { mask } from 'vue-the-mask';
import { mapActions, mapGetters } from 'vuex';
import { auth } from '^/auth';
import { axios } from '^/axios';
import { configuration } from '~/configuration';
import moment from 'moment-timezone';
import Schedule from '@/shop/rotating-subscription-partials/Schedule.vue';

const now = new Date();
const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
// 15th two months prior
const minDate = new Date(today);
const maxDate = new Date(today);
maxDate.setMonth(maxDate.getMonth() + 1);

moment.relativeTimeRounding(Math.floor);
moment.relativeTimeThreshold('s', 60);
moment.relativeTimeThreshold('m', 60);
moment.relativeTimeThreshold('h', 24);
moment.relativeTimeThreshold('d', 7);
moment.relativeTimeThreshold('w', 4);
moment.relativeTimeThreshold('M', 12);

const ORDER_TYPES = {
  delivery: 'Delivery',
  pickup: 'Pickup',
  shipping: 'Shipping',
};

const intervalOptions = {
  sunday: [],
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
};

const WEEK_DAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export default {
  components: {
    Schedule,
    Address,
    PaymentMethods,
    SubscriptionEditor,
    Spinner,
  },

  directives: { mask },

  data: () => ({
    options: [{ value: 'scheduled', text: 'Schedule this for later.' }],
    contactless_options: {
      leave: 'LeaveUnattended',
      attempt_accept: 'AttemptAccept',
      must_accept: 'MustAccept',
    },

    DELIVERY_TYPE_OPTIONS: {
      now: 'now',
      scheduled: 'scheduled',
      subscribe: 'subscribe',
    },
    address: {
      id: null,
      autocomplete: null,
      unit: null,
    },
    contact: {
      name: String(),
      email: String(),
      phone: String(),
    },
    pickupScheduleIntervals: {},
    deliveryScheduleIntervals: {},
    valid: false,
    loading: false,
    configuration,
    moment,
    minDate,
    maxDate,
    order: {
      deliveryType: 'scheduled',
      order_type: String(),
      code: null,
      promo_code: null,
      products: [],
      date: null,
      time: null,
      note: null,
      delivery_note: undefined,
      contactless_option: null,
      payment_method_uuid: String(),
      gift_card_credit: undefined,
      gift_card_uuid: undefined,
      tip: {
        value: Number(),
        type: Number(),
      },
      address_id: Number(),
      subscription: {
        days_of_week_times: new Array(7),
      },
    },
    productSubscription: [],
    noSlotsOnDay: false,
    selectedDaysQuantity: 0,
    ORDER_TYPES,
    intervalOptions,
    WEEK_DAYS,
    soonestCutOffProduct: null,
    soonestCutOff: null,
    rotatingSubscriptionSchedule: null,
    daysOfWeek: [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ],
  }),
  beforeDestroy() {
    // If order has been done, and customer leave order page, remove order data
    if (this.order.code) {
      Object.keys(this.order).forEach((key) => {
        this.order[key] = null;
      });
      localStorage.removeItem('state.checkout');
      localStorage.removeItem('state.cart');
    }
  },

  async mounted() {
    // Finix payment processor is no longer supported - removed initialization code

    if (!this.user) {
      return await this.$router.push({
        path: 'validate',
        query: { redirect: '/checkout' },
      });
    }
    if (this.cartProducts.length === 0) {
      await this.$store.dispatch(
        'alertError',
        'You don\'t have anything in your cart. Select some products on the shop page to proceed!'
      );
      return this.$router.push({
        path: '/',
      });
    }

    this.loading = true;

    const storedOrder = localStorage.getItem('state.checkout');
    const storedCart = JSON.parse(localStorage.getItem('state.cart'));

    if (storedCart) {
      if (storedCart.order_type) {
        this.$store.commit('setOrderType', storedCart.order_type);
      }

      if (storedCart.gift_card_credit) {
        this.order.gift_card_credit = Number(
          storedCart.gift_card_credit
        ).toFixed(2);
        this.order.gift_card_uuid = this.giftCards?.uuid;
      }

      if (storedCart.shipping_rate_id) {
        this.$store.commit('setShippingRateId', storedCart.shipping_rate_id);
      }
    }

    this.contact = { ...this.contact, ...this.user };
    this.order.contactless_option = this.contactless_options.attempt_accept;

    if (this.cartProducts.length && this.cartProducts[0].subscription) {
      this.order.deliveryType = this.DELIVERY_TYPE_OPTIONS.subscribe;
      const days = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ];

      days.forEach((dayKey, i) => {
        if (!this.cartProducts[0].subscription[dayKey]) return;
        var dayName =
          dayKey.charAt(0).toUpperCase() + dayKey.substring(1).toLowerCase();
        this.productSubscription = [
          ...this.productSubscription,
          { day: dayName, time: this.cartProducts[0].subscription[dayKey] },
        ];
      });
    }

    this.address = this.user.addresses.find(
      (address) => address.id === storedCart.address
    );

    await this.getVendorDeliveryScheduleIntervals();
    await this.getVendorPickupScheduleIntervals();

    if (storedOrder) {
      const { subscription, date, time, deliveryType, note, delivery_note } =
        JSON.parse(storedOrder);

      if (deliveryType) {
        this.order.deliveryType = deliveryType;
      } else {
        this.order.deliveryType =
          this.vendorRegion &&
          this.vendorRegion.default_order_cadence === 'Subscription'
            ? 'subscribe'
            : 'scheduled';
      }
      this.order.subscription = subscription || this.order.subscription;
      this.note = note || null;
      this.order.time = time || null;
      this.order.note = note || undefined;
      this.order.delivery_note = delivery_note || undefined;

      this.handleDateChange(date);
    } else {
      this.order.deliveryType =
        this.vendorRegion &&
        this.vendorRegion.default_order_cadence === 'Subscription'
          ? 'subscribe'
          : 'scheduled';
    }

    if (this.quote && Number(this.quote.discount)) {
      this.order.promo_code = this.discountCode;
    }

    this.order.tip = this.tip;

    this.order.order_type = this.orderType;

    let filtered = this.cartProducts.filter(
      (p) =>
        // Check if product has timespans with cutoff_at or legacy cut_off_time
        (p.timespans &&
          p.timespans.length &&
          p.timespans.some((t) => t.cutoff_at)) ||
        p.cut_off_time
    );

    if (filtered.length > 0) {
      let soonest = filtered.reduce((prev, curr) => {
        // If product has timespans with cutoff_at
        if (
          prev.timespans &&
          prev.timespans.length &&
          prev.timespans.some((t) => t.cutoff_at)
        ) {
          const prevCutoffTimespan = [...prev.timespans]
            .filter((t) => t.cutoff_at)
            .sort((a, b) => new Date(a.cutoff_at) - new Date(b.cutoff_at))[0];

          // If current also has timespans with cutoff_at
          if (
            curr.timespans &&
            curr.timespans.length &&
            curr.timespans.some((t) => t.cutoff_at)
          ) {
            const currCutoffTimespan = [...curr.timespans]
              .filter((t) => t.cutoff_at)
              .sort((a, b) => new Date(a.cutoff_at) - new Date(b.cutoff_at))[0];

            // Compare cutoff_at times
            return new Date(prevCutoffTimespan.cutoff_at) <
              new Date(currCutoffTimespan.cutoff_at)
              ? prev
              : curr;
          }

          // Compare timespan cutoff_at vs legacy cutoff fields
          if (curr.cut_off_time) {
            const currCutoffMoment = moment(curr.cut_off_time, 'HH:mm:ss')
              .isoWeekday(curr.cut_off_day)
              .set('week', moment().week());

            return new Date(prevCutoffTimespan.cutoff_at) < currCutoffMoment
              ? prev
              : curr;
          }

          return prev; // Keep prev if curr has no cutoff
        }

        // If current has timespans with cutoff_at but prev doesn't
        if (
          curr.timespans &&
          curr.timespans.length &&
          curr.timespans.some((t) => t.cutoff_at)
        ) {
          const currCutoffTimespan = [...curr.timespans]
            .filter((t) => t.cutoff_at)
            .sort((a, b) => new Date(a.cutoff_at) - new Date(b.cutoff_at))[0];

          // Compare legacy cutoff fields vs timespan cutoff_at
          if (prev.cut_off_time) {
            const prevCutoffMoment = moment(prev.cut_off_time, 'HH:mm:ss')
              .isoWeekday(prev.cut_off_day)
              .set('week', moment().week());

            return prevCutoffMoment < new Date(currCutoffTimespan.cutoff_at)
              ? prev
              : curr;
          }

          return curr; // Use curr if prev has no cutoff
        }

        // Legacy code - compare cut_off_day and cut_off_time
        if (prev.cut_off_day < curr.cut_off_day) return prev;
        if (prev.cut_off_day > curr.cut_off_day) return curr;

        return prev.cut_off_time < curr.cut_off_time ? prev : curr;
      });

      if (!this.vendor.settings.disable_cutoffs) {
        this.soonestCutOffProduct = soonest;

        // If product has timespans with cutoff_at
        if (
          soonest.timespans &&
          soonest.timespans.length &&
          soonest.timespans.some((t) => t.cutoff_at)
        ) {
          const cutoffTimespan = [...soonest.timespans]
            .filter((t) => t.cutoff_at)
            .sort((a, b) => new Date(a.cutoff_at) - new Date(b.cutoff_at))[0];

          // Get region's timezone - use IANA timezone identifier
          const timezone = this.region.timezone || 'UTC';
          
          this.soonestCutOff = moment.utc(cutoffTimespan.cutoff_at).tz(timezone);
        } else if (soonest.cut_off_time) {
          // Legacy code for cut_off_day and cut_off_time
          this.soonestCutOff = moment(soonest.cut_off_time, 'HH:mm:ss')
            .isoWeekday(soonest.cut_off_day)
            .set('week', moment().week());
          
          // Get region's timezone - use IANA timezone identifier
          const timezone = this.region.timezone || 'UTC';
          
          this.soonestCutOff = moment
            .utc(this.soonestCutOff.format('dddd HH:mm:ss'), 'dddd HH:mm:ss')
            .tz(timezone);
        }

        if (this.soonestCutOff) {
          const now = moment().tz(this.region.timezone);
          if (now.isAfter(this.soonestCutOff)) {
            this.$store.dispatch(
              'alertInfo',
              `${
                this.soonestCutOffProduct.title
              } must be ordered by ${this.soonestCutOff.format(
                'ddd'
              )} at ${this.soonestCutOff.format(
                'h:mma'
              )} to be received the following week.`
            );
          }
        }
      }
    }

    if (
      this.vendorRegion.enable_on_demand &&
      this.isVendorOpen(
        this.vendorRegion,
        this.orderType === ORDER_TYPES.delivery
      ) &&
      !this.soonestCutOff
    ) {
      this.options.push({ value: 'now', text: 'I want this right now.' });

      if (
        this.vendorRegion.default_order_cadence ===
        this.DELIVERY_TYPE_OPTIONS.now
      ) {
        this.order.deliveryType = this.DELIVERY_TYPE_OPTIONS.now;
      }
    }

    if (this.vendor.settings.enable_subscriptions) {
      this.options.push({ value: 'subscribe', text: 'Subscribe to this.' });

      if (
        this.vendorRegion.default_order_cadence ===
        this.DELIVERY_TYPE_OPTIONS.subscribe
      ) {
        this.order.deliveryType = this.DELIVERY_TYPE_OPTIONS.subscribe;
      }
    }

    this.compileIntervalOptions();
    if (this.rotatingSubscription) {
      this.rotatingSubscriptionSchedule = await this.getSubscription();
      this.order.deliveryType = this.DELIVERY_TYPE_OPTIONS.subscribe;

      // Make sure subscription object exists
      if (!this.order.subscription) {
        this.order.subscription = {};
      }

      // Initialize days_of_week_times as an array of nulls (required by API validation)
      this.order.subscription.days_of_week_times = [
        null,
        null,
        null,
        null,
        null,
        null,
        null,
      ];

      // Look for the day of week in the rotatingSubscriptionSchedule
      for (const day in this.deliveryScheduleIntervals) {
        const dayLower = day.toLowerCase();
        if (this.rotatingSubscriptionSchedule[dayLower]) {
          const interval = this.rotatingSubscriptionSchedule[dayLower];
          const dayIndex = this.daysOfWeek.indexOf(dayLower);

          // Set the time in the proper day index
          if (dayIndex >= 0) {
            this.order.subscription.days_of_week_times[dayIndex] = interval;
          }

          // Format visual elements for display (not related to the actual API payload)
          const formattedTime = moment(interval, 'HH:mm:ss').format('h:mma');
          const matchedSlot = Object.keys(
            this.deliveryScheduleIntervals[day]
          ).find((slot) => slot.startsWith(formattedTime));

          this.deliveryScheduleIntervals[day] = {};
          this.deliveryScheduleIntervals[day][matchedSlot] = interval;
          this.pickupScheduleIntervals[day][matchedSlot] =
            this.deliveryScheduleIntervals[day];
        } else {
          this.deliveryScheduleIntervals[day] = this.pickupScheduleIntervals[
            day
          ] = {};
        }
      }
    }
    this.$forceUpdate();
    this.loading = false;
  },

  computed: {
    ...mapGetters([
      'orderAddress',
      'user',
      'vendor',
      'vendorRegion',
      'region',
      'regionId',
      'cartProducts',
      'discountCode',
      'tip',
      'orderType',
      'shippingRateId',
      'quote',
      'giftCards',
      'cart',
      'rotatingSubscription',
    ]),

    stateEmail: function () {
      const re = new RegExp('^[\\w|+]+@([\\w-]+\\.)+[\\w]+$', 'gi');
      return re.test(this.contact.email);
    },

    vendorUuid() {
      return this.vendor.uuid;
    },

    prepTimeInMinutes() {
      return this.vendorRegion.prep_time_minutes;
    },

    isFormValid() {
      if (
        this.order.deliveryType === this.DELIVERY_TYPE_OPTIONS.scheduled &&
        !(this.order.date && this.order.time)
      )
        return false;
      if (
        this.order.deliveryType === this.DELIVERY_TYPE_OPTIONS.subscribe &&
        !this.order.subscription
      )
        return false;
      if (
        this.order.order_type === this.ORDER_TYPES.delivery &&
        !this.address?.id
      )
        return false;
      if (
        this.order.order_type === this.ORDER_TYPES.shipping &&
        !this.address?.id
      )
        return false;
      return this.cartProducts.length > 0 && this.order.payment_method_uuid;
    },

    subscribedDays() {
      const formatedSub = this.order.subscription.days_of_week_times
        .map((value, index) => [WEEK_DAYS[index], value])
        .filter(Boolean);
      return Object.fromEntries(formatedSub);
    },

    disableCheckout() {
      if (!this.isFormValid) return true;
      return (
        !this.cartProducts[0].subscription &&
        this.order.subscription &&
        this.order.deliveryType === this.DELIVERY_TYPE_OPTIONS.subscribe &&
        Object.values(this.order.subscription.days_of_week_times).filter(
          Boolean
        ).length < (this.cartProducts[0]?.max_weekly_frequency || 1)
      );
    },
    weeklyFrequency() {
      return this.rotatingSubscription
        ? 1
        : (this.cartProducts.length === 1 &&
            this.cartProducts[0].max_weekly_frequency) ||
            null;
    },
  },

  methods: {
    ...mapActions(['hideAlert', 'alertError']),

    isVendorOpen(vendorRegion, isDelivery) {
      const currentDateTime = moment().tz(this.region.timezone);
      const schedules = isDelivery
        ? this.$store.getters?.vendorRegion?.delivery_schedules
        : this.$store.getters?.vendorRegion?.schedules;

      for (const schedule of schedules) {
        const startTime = moment(schedule.start, 'HH:mm:ss');
        const endTime = moment(schedule.end, 'HH:mm:ss');

        if (
          schedule.day === currentDateTime.day() &&
          startTime.isSameOrBefore(currentDateTime) &&
          endTime.isSameOrAfter(currentDateTime)
        ) {
          const closingTime = endTime
            .clone()
            .subtract(vendorRegion.prep_time_minutes, 'minutes');

          if (closingTime.isAfter(currentDateTime)) {
            return true;
          }
        }
      }

      return false;
    },

    async getVendorDeliveryScheduleIntervals() {
      this.deliveryScheduleIntervals = await axios.get(
        `region/${this.region.uuid}/vendor/${this.vendorUuid}/schedule/delivery-intervals`,
        {
          params: {
            prep_time_minutes: this.prepTimeInMinutes,
          },
        }
      );
    },

    async getVendorPickupScheduleIntervals() {
      this.pickupScheduleIntervals = await axios.get(
        `region/${this.region.uuid}/vendor/${this.vendorUuid}/schedule/pickup-intervals`,
        {
          params: {
            prep_time_minutes: this.prepTimeInMinutes,
          },
        }
      );
    },

    updateUser() {
      return axios.put('user', {
        ...this.user,
        ...this.contact,
        addresses: [
          {
            user_id: this.user.id,
            address_id: this.address.id,
          },
        ],
      });
    },

    handleDateChange(value) {
      const day = moment(value).format('dddd');
      const dayLower = day.toLowerCase();

      this.intervalOptions[day.toLowerCase()] = this.getIntervalOptions(day);

      this.noSlotsOnDay =
        !this.intervalOptions[dayLower] ||
        !this.intervalOptions[dayLower].length;

      if (!this.noSlotsOnDay && !this.order.time)
        this.order.time = this.intervalOptions[dayLower][0].value;
    },

    async checkout() {
      try {
        this.$store.dispatch('hideAlert');
        this.loading = true;

        if (
          this.order.order_type === this.ORDER_TYPES.delivery ||
          this.order.order_type === this.ORDER_TYPES.shipping
        )
          this.order.address_id = this.address ? this.address.id : null;
        else {
          delete this.order.address_id;
          this.order.contactless_option = null;
        }

        if (this.order.order_type === this.ORDER_TYPES.shipping) {
          this.order.shipping_rate_id = this.shippingRateId;
        }

        if (this.order.order_type !== this.ORDER_TYPES.delivery) {
          this.order.delivery_note = undefined;
        }

        this.order.products = this.cartProducts.map((x) => ({
          product_uuid: x.uuid,
          product_favorite: x.favorite || null,
          product_quantity: x.quantityOrdered,
          product_modifiers: x.modifiers
            .map((x) => x.items)
            .flat()
            .filter((x) => x.item_quantity > 0 || x.item_quantity < 0)
            .map((x) => ({
              item_uuid: x.uuid,
              item_quantity: x.item_quantity,
            })),
        }));

        if (this.order.deliveryType !== this.DELIVERY_TYPE_OPTIONS.subscribe) {
          delete this.order.subscription;
        } else {
          // Always ensure a subscription object exists
          if (!this.order.subscription) {
            this.order.subscription = {};
          }

          // Ensure days_of_week_times is formatted correctly as an array of 7 items (potentially null)
          if (!this.order.subscription.days_of_week_times) {
            this.order.subscription.days_of_week_times = [
              null,
              null,
              null,
              null,
              null,
              null,
              null,
            ];
          }

          // Convert object format to array if needed
          if (
            typeof this.order.subscription.days_of_week_times === 'object' &&
            !Array.isArray(this.order.subscription.days_of_week_times)
          ) {
            // If it's an object with day names as keys, convert to array
            const daysArray = [null, null, null, null, null, null, null];
            Object.entries(this.order.subscription.days_of_week_times).forEach(
              ([day, time]) => {
                if (time) {
                  const dayIndex = this.daysOfWeek.indexOf(day.toLowerCase());
                  if (dayIndex >= 0) {
                    daysArray[dayIndex] = time;
                  }
                }
              }
            );
            this.order.subscription.days_of_week_times = daysArray;
          }

          // If array isn't exactly 7 elements, ensure it's properly sized
          if (
            Array.isArray(this.order.subscription.days_of_week_times) &&
            this.order.subscription.days_of_week_times.length !== 7
          ) {
            const currentArray = [
              ...this.order.subscription.days_of_week_times,
            ];
            this.order.subscription.days_of_week_times = [
              null,
              null,
              null,
              null,
              null,
              null,
              null,
            ];
            currentArray.forEach((time, index) => {
              if (index < 7) {
                this.order.subscription.days_of_week_times[index] = time;
              }
            });
          }
        }

        if (this.order.deliveryType !== this.DELIVERY_TYPE_OPTIONS.scheduled) {
          this.order.date = null;
          this.order.time = null;
        }

        if (this.rotatingSubscription) {
          this.order.rotating_subscription = this.rotatingSubscription;
        }

        const response = await axios.post(
          `region/${this.region.uuid}/vendor/${this.vendorUuid}/order`,
          this.order
        );

        this.$store.commit('clearCart');
        this.$store.dispatch('getGiftCardsInfo');
        this.order = response;
        localStorage.removeItem('state.checkout');
        localStorage.removeItem('state.cart');
      } catch (e) {
        this.$store.dispatch('alertError', e.message);
      } finally {
        this.loading = false;
        window.scrollTo(0, 0);
      }
    },
    dateDisabled(_, date) {
      const now = moment().tz(this.region.timezone);
      const momentDate = moment(date).startOf('day');
      const dateStr = momentDate.format('YYYY-MM-DD');
      const day = momentDate.day(); // 0 = Sunday, 1 = Monday, etc.
      let dayName = momentDate.format('dddd').toLowerCase();
      dayName = dayName.charAt(0).toUpperCase() + dayName.substring(1).toLowerCase();
      // Check if the date is in the past
      if (momentDate.isBefore(now.startOf('day'))) {
        return true;
      }
      
      // Check if vendor has schedules for this day based on order type
      let hasSchedulesForDay = false;
      if (this.orderType === ORDER_TYPES.delivery) {
        // Check if vendor has delivery schedules for this day
        hasSchedulesForDay = this.deliveryScheduleIntervals[dayName] && 
                            Object.keys(this.deliveryScheduleIntervals[dayName]).length > 0;
      } else if (this.orderType === ORDER_TYPES.pickup) {
        // Check if vendor has pickup schedules for this day
        hasSchedulesForDay = this.pickupScheduleIntervals[dayName] && 
                            Object.keys(this.pickupScheduleIntervals[dayName]).length > 0;
      }

      // If no schedules for this day, disable the date
      if (!hasSchedulesForDay) {
        return true;
      }

      // Get products with timespans
      const productsWithTimespans = this.cartProducts.filter(
        p => p.timespans && p.timespans.length > 0
      );
      
      // If no products have timespans, only check vendor schedules (which we already did)
      if (productsWithTimespans.length === 0) {
        return !hasSchedulesForDay;
      }
      
      // Check if date falls within the service period of any product's timespans
      // and also check that we haven't passed the cutoff time for that timespan
      const dateWithinValidTimespan = productsWithTimespans.some(product => {
        return product.timespans.some(timespan => {
          // Get region's timezone - use IANA timezone identifier
          const timezone = this.region.timezone || 'UTC';
          
          // Parse beginningAt as UTC and convert to region timezone
          const beginningAt = moment.utc(timespan.beginning_at).tz(timezone);
          
          // If ending_at is null, it means the product is indefinitely available
          let inEndingDateRange = true;
          if (timespan.ending_at) {
            const endingAt = moment.utc(timespan.ending_at).tz(timezone);
            inEndingDateRange = dateStr <= endingAt.format('YYYY-MM-DD');
          }

          // Check if the date is within the service period
          const inServicePeriod = dateStr >= beginningAt.format('YYYY-MM-DD') && inEndingDateRange;
          
          // Check if we're before the cutoff time
          const cutoffAt = timespan.cutoff_at ? 
            moment.utc(timespan.cutoff_at).tz(timezone) : null;
          const beforeCutoff = !cutoffAt || now.isBefore(cutoffAt);
          
          return inServicePeriod && beforeCutoff;
        });
      });
      
      // Date is disabled if:
      // 1. No vendor schedules for this day, OR
      // 2. No valid product timespans for this day
      return !hasSchedulesForDay || !dateWithinValidTimespan;
    },

    canSubOnDay(day) {
      if (this.order.order_type === this.ORDER_TYPES.delivery)
        return this.$store.getters.vendorRegion.delivery_schedules.find(
          (x) => x.day === day
        );
      else if (this.order.order_type === this.ORDER_TYPES.pickup)
        return this.$store.getters.vendorRegion.schedules.find(
          (x) => x.day === day
        );
    },

    compileIntervalOptions(type = this.order.order_type) {
      moment
        .weekdays()
        .forEach(
          (weekday) =>
            (this.intervalOptions[weekday.toLowerCase()] =
              this.getIntervalOptions(weekday, null, type))
        );
    },

    getIntervalOptions(
      day,
      date = this.order.date,
      type = this.order.order_type
    ) {
      const options = [];
      let schedules = [];

      if (type === this.ORDER_TYPES.delivery) {
        schedules = this.deliveryScheduleIntervals[day];
      } else if (
        type === this.ORDER_TYPES.pickup ||
        type === this.ORDER_TYPES.shipping
      ) {
        schedules = this.pickupScheduleIntervals[day];
      }

      // No date selected or no schedules
      if (!date || !schedules) {
        return options;
      }

      const now = moment().tz(this.region.timezone);
      const minimumTime = now.clone().add(this.prepTimeInMinutes, 'minutes');
      const selectedDate = moment(date).format('YYYY-MM-DD');

      // Get products with timespans
      const productsWithTimespans = this.cartProducts.filter(
        (p) => p.timespans && p.timespans.length > 0
      );

      // Find all active timespans for this date
      const activeTimespans = productsWithTimespans.flatMap((product) =>
        product.timespans.filter((timespan) => {
          // Get region's timezone - use IANA timezone identifier
          const timezone = this.region.timezone || 'UTC';
          
          // Parse beginningAt as UTC and convert to region timezone
          const beginningAt = moment.utc(timespan.beginning_at).tz(timezone);
          
          // If ending_at is null, it means the product is indefinitely available
          let inEndingDateRange = true;
          if (timespan.ending_at) {
            const endingAt = moment.utc(timespan.ending_at).tz(timezone);
            inEndingDateRange = selectedDate <= endingAt.format('YYYY-MM-DD');
          }

          // Check if the selected date is within the service period
          return (
            selectedDate >= beginningAt.format('YYYY-MM-DD') &&
            inEndingDateRange &&
            (!timespan.cutoff_at || now.isBefore(
              moment.utc(timespan.cutoff_at).tz(timezone)
            ))
          );
        })
      );

      // Process schedule slots
      if (schedules) {
        const scheduleKeys = Object.keys(schedules);
        const scheduleValues = Object.values(schedules);

        scheduleKeys.forEach((key, index) => {
          const schedule = scheduleValues[index];
          // Create a moment object for the schedule time on the selected date
          const scheduleDateTime = moment(
            `${selectedDate} ${schedule}`,
            'YYYY-MM-DD HH:mm:ss'
          ).tz(this.region.timezone);
          const scheduleTime = scheduleDateTime.format('HH:mm:ss');

          // Check if this time slot is after the minimum prep time (for today)
          let isPastPrepTime = false;
          if (
            now.format('YYYY-MM-DD') === selectedDate &&
            this.order.deliveryType === this.DELIVERY_TYPE_OPTIONS.now
          ) {
            isPastPrepTime = scheduleDateTime.isBefore(minimumTime);
          }

          // Check if this time slot is within any active timespan
          let withinTimespan = true;

          if (activeTimespans.length > 0) {
            withinTimespan = activeTimespans.some((timespan) => {
              const beginningAt = moment
                .tz(timespan.beginning_at, 'UTC')
                .tz(this.region.timezone);
              
              // On the beginning date, check time is after beginning time
              if (selectedDate === beginningAt.format('YYYY-MM-DD')) {
                return scheduleTime >= beginningAt.format('HH:mm:ss');
              }

              // On the ending date, check time is before ending time
              if (
                timespan.ending_at &&
                selectedDate === moment.tz(timespan.ending_at, 'UTC').tz(this.region.timezone).format('YYYY-MM-DD')
              ) {
                const endingAt = moment.tz(timespan.ending_at, 'UTC').tz(this.region.timezone);
                return scheduleTime <= endingAt.format('HH:mm:ss');
              }

              // Date is between beginning and ending, so any time is valid
              return true;
            });
          } else if (productsWithTimespans.length > 0) {
            // If there are products with timespans but none are active for this date, disable all slots
            withinTimespan = false;
          }

          // Add the option if it passes all checks
          if (!isPastPrepTime && withinTimespan) {
            options.push({ text: key, value: schedule });
          }
        });
      }

      // If we have no slots but there are active timespans, provide a default slot
      // This is useful for dates that fall within timespan periods but aren't in regular schedule
      if (options.length === 0 && activeTimespans.length > 0) {
        options.push({
          text: '8:00am',
          value: '08:00:00',
        });
      }

      // Set default time option if needed
      if (
        this.order.deliveryType === this.DELIVERY_TYPE_OPTIONS.scheduled &&
        options.length > 0 &&
        !this.order.time
      ) {
        this.order.time = options[0].value;
      }

      return options;
    },
    updateSelectedDays(newData) {
      this.selectedDaysQuantity = newData;
    },

    async getSubscription() {
      return await axios.get(
        `/rotating-subscription/${this.rotatingSubscription.uuid}/subscription`
      );
    },

    getFormattedFulfillmentDate() {
      // Fallback method to format the fulfillment date if the API doesn't provide it
      if (
        this.rotatingSubscription &&
        this.rotatingSubscription.next_fulfillment_datetime
      ) {
        // Get region's timezone - use IANA timezone identifier
        let regionTimezone =
          this.rotatingSubscription.region_timezone ||
          this.region.timezone ||
          'UTC';
          
        return moment
          .utc(this.rotatingSubscription.next_fulfillment_datetime)
          .tz(regionTimezone)
          .format('dddd, MMMM D [at] h:mma');
      }

      // Default case for rotating subscriptions with schedule data
      if (this.rotatingSubscriptionSchedule) {
        // Find the day that has a time set
        for (const day of Object.keys(this.rotatingSubscriptionSchedule)) {
          if (this.rotatingSubscriptionSchedule[day]) {
            const dayIndex = this.daysOfWeek.indexOf(day);
            if (dayIndex !== -1) {
              // Calculate the next occurrence of this day
              const nextDate = moment().day(dayIndex);
              // If today is past the day, get next week
              if (moment().day() > dayIndex) {
                nextDate.add(1, 'week');
              }

              // Format with month and day
              return (
                nextDate.format('dddd, MMMM D [at] ') +
                moment(
                  this.rotatingSubscriptionSchedule[day],
                  'HH:mm:ss'
                ).format('h:mma')
              );
            }
          }
        }
      }

      return 'your scheduled delivery date';
    },
  },
  watch: {
    'order.order_type'(value) {
      if (this.options[0].disabled) this.order.deliveryType = 'subscribe';

      this.compileIntervalOptions(value);
    },
    'order.date'(value) {
      this.handleDateChange(value);
    },
    order: {
      handler(value) {
        this.$nextTick(() => {
          localStorage.setItem('state.checkout', JSON.stringify(value));
        });
      },
      deep: true,
    },
  },
};
</script>
<style lang="scss" scoped>
@import '../../styles/variables.module.scss';

@include inputs;
@include buttons;

.checkout-container {
  p {
    font-size: 1.25rem;
  }

  #textarea {
    textarea {
      border-radius: 5px !important;
    }
  }
}

.hours-of-operation {
  border-top-left-radius: 5px !important;
  border-top-right-radius: 5px !important;
  letter-spacing: 0.065rem;
  background-color: black;
  text-decoration: underline;
  color: white;
  font-family: $staatliches-font;
  font-weight: 900;
  font-size: 1.25rem;
}

.schedule {
  border-bottom-left-radius: 5px !important;
  border-bottom-right-radius: 5px !important;
}

.date-picker.form-control {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.rounded-end {
  font-family: $staatliches-font;
  font-weight: 900;
  overflow: hidden !important;
  border-top-right-radius: 25px !important;
  border-bottom-right-radius: 25px !important;
}

.type-button {
  &:active,
  &:focus {
    color: white !important;
    background-color: var(--primary) !important;
    border-color: var(--primary) !important;
  }
}

.time {
  width: 140px;
  display: block;
  float: left;
  font-size: 1.05em;
  font-weight: bold;
  letter-spacing: 0.075em;
}

.fixed-fulfillment-date {
  background-color: #2c2c2c;
  color: white;
  border-radius: 4px;
  padding: 12px 15px;
  margin: 10px 0;
  text-align: center;
}

.fixed-fulfillment-text {
  margin: 0;
  font-size: 1.1em;
  font-weight: 500;
}

@media only screen and (min-width: 992px) {
  .mt-32 {
    margin-top: 32px;
  }
}

.order-type {
  width: 100%;

  button {
    display: inline-block;
    width: 100%;
    max-width: 240px;
  }
}

.sub-info {
  padding: 7px 14px;
  border: 2px solid var(--primary);
  border-radius: 8px !important;
  // color: white;
  font-weight: bold;
}
</style>
